import React, { useState, useEffect } from 'react';
import './BOMComponents.css';
import API_BASE_URL from '../config';

const BOMComponents = () => {
  const [activeTab, setActiveTab] = useState('bom');
  const [components, setComponents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingComponent, setEditingComponent] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterSupplier, setFilterSupplier] = useState('');

  // BOM-specific state
  const [bomSkus, setBomSkus] = useState([]);
  const [selectedSku, setSelectedSku] = useState('');
  const [bomStructure, setBomStructure] = useState(null);
  const [bomLoading, setBomLoading] = useState(false);
  const [showBomForm, setShowBomForm] = useState(false);
  const [editingBomEntry, setEditingBomEntry] = useState(null);
  const [availableComponents, setAvailableComponents] = useState([]);
  const [bomFormData, setBomFormData] = useState({
    parent_id: '',
    component_id: '',
    BOM_quantity: 1,
    BOM_category: 'Component',
    BOM_unit: 'units'
  });
  const [allAvailableParents, setAllAvailableParents] = useState([]);

  // Sub-assemblies state
  const [subAssemblies, setSubAssemblies] = useState([]);
  const [selectedSubAssembly, setSelectedSubAssembly] = useState('');
  const [selectedSubAssemblyStructure, setSelectedSubAssemblyStructure] = useState(null);
  const [editingSubAssembly, setEditingSubAssembly] = useState(null);
  const [showAddChildForm, setShowAddChildForm] = useState(false);
  const [newChildComponent, setNewChildComponent] = useState({ component_id: '', quantity: 1 });
  const [subAssemblyLoading, setSubAssemblyLoading] = useState(false);
  const [showSubAssemblyForm, setShowSubAssemblyForm] = useState(false);
  const [editingSubAssemblyEntry, setEditingSubAssemblyEntry] = useState(null);
  const [subAssemblyFormData, setSubAssemblyFormData] = useState({
    parent_id: '',
    component_id: '',
    BOM_quantity: 1,
    BOM_category: 'Sub-assembly',
    BOM_unit: 'units'
  });

  // New sub-assemblies table state
  const [subAssembliesTable, setSubAssembliesTable] = useState([]);
  const [showSubAssemblyTableForm, setShowSubAssemblyTableForm] = useState(false);
  const [editingSubAssemblyTable, setEditingSubAssemblyTable] = useState(null);
  const [subAssemblyTableFormData, setSubAssemblyTableFormData] = useState({
    sub_assembly: '',
    sub_category: ''
  });
  const [subAssemblyTableLoading, setSubAssemblyTableLoading] = useState(false);

  // Swap functionality state
  const [showSwapModal, setShowSwapModal] = useState(false);
  const [swapTarget, setSwapTarget] = useState(null);
  const [availableSwapComponents, setAvailableSwapComponents] = useState([]);
  const [selectedSwapComponent, setSelectedSwapComponent] = useState('');
  const [swapLoading, setSwapLoading] = useState(false);

  // Form state for adding/editing components
  const [formData, setFormData] = useState({
    component_id: '',
    supplier: '',
    supplier_number: '',
    component_description: '',
    MOQ: 1,
    lead_time_weeks: 1,
    is_inventoried: true
  });

  // Add Item functionality state
  const [showAddItemModal, setShowAddItemModal] = useState(false);
  const [addItemFormData, setAddItemFormData] = useState({
    parent_id: '',
    component_id: '',
    BOM_quantity: 1,
    BOM_category: '',
    BOM_unit: ''
  });
  const [availableParents, setAvailableParents] = useState([]);
  const [availableSkus, setAvailableSkus] = useState([]);
  const [availableSubAssemblies, setAvailableSubAssemblies] = useState([]);
  const [existingCategories, setExistingCategories] = useState([]);
  const [existingUnits, setExistingUnits] = useState([]);
  const [addItemLoading, setAddItemLoading] = useState(false);

  // Fetch components from the backend
  const fetchComponents = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/components`);
      if (!response.ok) {
        throw new Error('Failed to fetch components');
      }
      const data = await response.json();
      setComponents(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Fetch BOM SKUs
  const fetchBomSkus = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/bom/skus`);
      if (!response.ok) {
        throw new Error('Failed to fetch BOM SKUs');
      }
      const data = await response.json();
      setBomSkus(data);
    } catch (err) {
      setError(err.message);
    }
  };

  // Fetch BOM structure for selected SKU
  const fetchBomStructure = async (skuNumber) => {
    if (!skuNumber) return;
    
    try {
      setBomLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/bom/structure/${skuNumber}`);
      if (!response.ok) {
        throw new Error('Failed to fetch BOM structure');
      }
      const data = await response.json();
      setBomStructure(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setBomLoading(false);
    }
  };

  // Fetch available components for BOM
  const fetchAvailableComponents = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/bom/components`);
      if (!response.ok) {
        throw new Error('Failed to fetch available components');
      }
      const data = await response.json();
      setAvailableComponents(data);
    } catch (err) {
      setError(err.message);
    }
  };

  // Fetch all available parents (SKUs and sub-assemblies)
  const fetchAllAvailableParents = async () => {
    try {
      // Fetch SKUs
      const skusResponse = await fetch(`${API_BASE_URL}/api/bom/skus`);
      if (!skusResponse.ok) {
        throw new Error('Failed to fetch SKUs');
      }
      const skus = await skusResponse.json();

      // Fetch sub-assemblies
      const subAssembliesResponse = await fetch(`${API_BASE_URL}/api/bom/sub-assemblies`);
      if (!subAssembliesResponse.ok) {
        throw new Error('Failed to fetch sub-assemblies');
      }
      const subAssemblies = await subAssembliesResponse.json();

      // Combine SKUs and sub-assemblies
      const allParents = [
        ...skus.map(sku => ({
          id: sku.IGD_SKU_number,
          name: `${sku.IGD_SKU_number} - ${sku.IGD_Name}`,
          type: 'SKU'
        })),
        ...subAssemblies.map(sub => ({
          id: sub.component_id,
          name: `${sub.component_id} - ${sub.component_description}`,
          type: 'Sub-assembly'
        }))
      ];

      setAllAvailableParents(allParents);
    } catch (err) {
      setError(err.message);
    }
  };

  // Fetch sub-assemblies
  const fetchSubAssemblies = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/bom/sub-assemblies`);
      if (!response.ok) {
        throw new Error('Failed to fetch sub-assemblies');
      }
      const data = await response.json();
      setSubAssemblies(data);
    } catch (err) {
      setError(err.message);
    }
  };

  // Fetch sub-assembly structure for selected component
  const fetchSubAssemblyStructure = async (componentId) => {
    if (!componentId) return;
    
    try {
      setSubAssemblyLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/bom/sub-assembly/${componentId}`);
      if (!response.ok) {
        throw new Error('Failed to fetch sub-assembly structure');
      }
      const data = await response.json();
      setSelectedSubAssemblyStructure(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubAssemblyLoading(false);
    }
  };

  // Fetch sub-assemblies table data
  const fetchSubAssembliesTable = async () => {
    try {
      setSubAssemblyTableLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/sub-assemblies`);
      if (!response.ok) {
        throw new Error('Failed to fetch sub-assemblies table');
      }
      const data = await response.json();
      setSubAssembliesTable(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubAssemblyTableLoading(false);
    }
  };

  // Fetch data for Add Item modal
  const fetchAddItemData = async () => {
    try {
      setAddItemLoading(true);
      
      // Fetch all SKUs
      const skusResponse = await fetch(`${API_BASE_URL}/api/skus`);
      if (skusResponse.ok) {
        const skus = await skusResponse.json();
        setAvailableSkus(skus);
      }
      
      // Fetch all components
      const componentsResponse = await fetch(`${API_BASE_URL}/api/components`);
      if (componentsResponse.ok) {
        const components = await componentsResponse.json();
        setAvailableComponents(components);
      }
      
      // Fetch all sub-assemblies from sub_assemblies table
      const subAssembliesResponse = await fetch(`${API_BASE_URL}/api/sub-assemblies`);
      if (subAssembliesResponse.ok) {
        const subAssemblies = await subAssembliesResponse.json();
        setAvailableSubAssemblies(subAssemblies);
      }
      
      // Get available parents from current BOM structure
      if (selectedSku && bomStructure?.structure) {
        const parents = [];
        
        // Add the SKU itself as a parent
        parents.push({
          id: selectedSku,
          name: bomStructure.sku_name || selectedSku,
          type: 'SKU'
        });
        
        // Add all components and sub-assemblies from the BOM structure
        const extractParents = (nodes) => {
          nodes.forEach(node => {
            parents.push({
              id: node.component_id,
              name: node.component_name || node.component_id,
              type: node.BOM_category || 'Component'
            });
            
            // Recursively add children as potential parents
            if (node.children && node.children.length > 0) {
              extractParents(node.children);
            }
          });
        };
        
        extractParents(bomStructure.structure);
        setAvailableParents(parents);
      }
      
      // Fetch existing categories and units from BOM structure
      const categoriesResponse = await fetch(`${API_BASE_URL}/api/bom/categories`);
      if (categoriesResponse.ok) {
        const categories = await categoriesResponse.json();
        setExistingCategories(categories);
      }
      
      const unitsResponse = await fetch(`${API_BASE_URL}/api/bom/units`);
      if (unitsResponse.ok) {
        const units = await unitsResponse.json();
        setExistingUnits(units);
      }
      
    } catch (err) {
      setError(err.message);
    } finally {
      setAddItemLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'components') {
      fetchComponents();
    } else if (activeTab === 'bom') {
      fetchBomSkus();
      fetchAvailableComponents();
      fetchAllAvailableParents();
    } else if (activeTab === 'sub-assemblies') {
      fetchSubAssembliesTable();
    }
  }, [activeTab]);

  useEffect(() => {
    if (selectedSku) {
      fetchBomStructure(selectedSku);
    }
  }, [selectedSku]);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'MOQ' || name === 'lead_time_weeks' ? parseInt(value) || 0 : value
    }));
  };

  // Handle BOM form input changes
  const handleBomInputChange = (e) => {
    const { name, value } = e.target;
    setBomFormData(prev => ({
      ...prev,
      [name]: name === 'BOM_quantity' ? parseFloat(value) || 0 : value
    }));
  };

  // Handle sub-assembly form input changes
  const handleSubAssemblyInputChange = (e) => {
    const { name, value } = e.target;
    setSubAssemblyFormData(prev => ({
      ...prev,
      [name]: name === 'BOM_quantity' ? parseFloat(value) || 0 : value
    }));
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      component_id: '',
      supplier: '',
      supplier_number: '',
      component_description: '',
      MOQ: 1,
      lead_time_weeks: 1,
      is_inventoried: true
    });
    setEditingComponent(null);
    setShowAddForm(false);
  };

  // Reset BOM form
  const resetBomForm = () => {
    setBomFormData({
      parent_id: selectedSku || '',
      component_id: '',
      BOM_quantity: 1,
      BOM_category: 'Component',
      BOM_unit: 'units'
    });
    setEditingBomEntry(null);
    setShowBomForm(false);
  };

  // Reset sub-assembly form
  const resetSubAssemblyForm = () => {
    setSubAssemblyFormData({
      parent_id: selectedSku || '',
      component_id: '',
      BOM_quantity: 1,
      BOM_category: 'Sub-assembly',
      BOM_unit: 'units'
    });
    setShowSubAssemblyForm(false);
  };

  // Add new component
  const handleAddComponent = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API_BASE_URL}/api/components`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to add component');
      }

      await fetchComponents();
      resetForm();
    } catch (err) {
      setError(err.message);
    }
  };

  // Update existing component
  const handleUpdateComponent = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API_BASE_URL}/api/components/${editingComponent.component_id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update component');
      }

      await fetchComponents();
      resetForm();
    } catch (err) {
      setError(err.message);
    }
  };

  // Delete component
  const handleDeleteComponent = async (componentId) => {
    if (!window.confirm(`Are you sure you want to delete component ${componentId}?`)) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/components/${componentId}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error('Failed to delete component');
      }

      await fetchComponents();
    } catch (err) {
      setError(err.message);
    }
  };

  // Start editing component
  const handleEditComponent = (component) => {
    setFormData({
      component_id: component.component_id,
      supplier: component.supplier || '',
      supplier_number: component.supplier_number || '',
      component_description: component.component_description || '',
      MOQ: component.MOQ || 1,
      lead_time_weeks: component.lead_time_weeks !== undefined ? component.lead_time_weeks : 1,
      is_inventoried: component.is_inventoried !== undefined ? component.is_inventoried : true
    });
    setEditingComponent(component);
    setShowAddForm(true);
  };

  // Add new BOM entry
  const handleAddBomEntry = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API_BASE_URL}/api/bom/entries`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(bomFormData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to add BOM entry');
      }

      await fetchBomStructure(selectedSku);
      resetBomForm();
    } catch (err) {
      setError(err.message);
    }
  };

  // Update existing BOM entry
  const handleUpdateBomEntry = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API_BASE_URL}/api/bom/entries/${editingBomEntry.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(bomFormData),
      });

      if (!response.ok) {
        throw new Error('Failed to update BOM entry');
      }

      await fetchBomStructure(selectedSku);
      resetBomForm();
    } catch (err) {
      setError(err.message);
    }
  };

  // Delete BOM entry
  const handleDeleteBomEntry = async (entryId) => {
    if (!window.confirm('Are you sure you want to delete this BOM entry?')) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/bom/entries/${entryId}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete BOM entry');
      }

      await fetchBomStructure(selectedSku);
    } catch (err) {
      setError(err.message);
    }
  };

  // Start editing BOM entry
  const handleEditBomEntry = (entry) => {
    setBomFormData({
      parent_id: entry.parent_id,
      component_id: entry.component_id,
      BOM_quantity: entry.BOM_quantity,
      BOM_category: entry.BOM_category,
      BOM_unit: entry.BOM_unit
    });
    setEditingBomEntry(entry);
    setShowBomForm(true);
  };

  // Add new sub-assembly entry
  const handleAddSubAssemblyEntry = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API_BASE_URL}/api/bom/entries`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(subAssemblyFormData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to add sub-assembly entry');
      }

      await fetchSubAssemblyStructure(selectedSubAssembly);
      await fetchSubAssemblies(); // Refresh the sub-assemblies list
      resetSubAssemblyForm();
    } catch (err) {
      setError(err.message);
    }
  };

  // Update existing sub-assembly entry
  const handleUpdateSubAssemblyEntry = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API_BASE_URL}/api/bom/entries/${editingSubAssemblyEntry.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(subAssemblyFormData),
      });

      if (!response.ok) {
        throw new Error('Failed to update sub-assembly entry');
      }

      await fetchSubAssemblyStructure(selectedSubAssembly);
      resetSubAssemblyForm();
    } catch (err) {
      setError(err.message);
    }
  };

  // Delete sub-assembly entry
  const handleDeleteSubAssemblyEntry = async (entryId) => {
    if (!window.confirm('Are you sure you want to delete this sub-assembly entry?')) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/bom/entries/${entryId}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete sub-assembly entry');
      }

      await fetchSubAssemblyStructure(selectedSubAssembly);
      await fetchSubAssemblies(); // Refresh the sub-assemblies list
    } catch (err) {
      setError(err.message);
    }
  };

  // Start editing sub-assembly entry
  const handleEditSubAssemblyEntry = (entry) => {
    setSubAssemblyFormData({
      parent_id: entry.parent_id,
      component_id: entry.component_id,
      BOM_quantity: entry.BOM_quantity,
      BOM_category: entry.BOM_category,
      BOM_unit: entry.BOM_unit
    });
    setEditingSubAssemblyEntry(entry);
    setShowSubAssemblyForm(true);
  };

  // Add new sub-assembly
  const handleAddSubAssembly = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API_BASE_URL}/api/bom/entries`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(subAssemblyFormData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to add sub-assembly');
      }

      await fetchBomStructure(selectedSku);
      resetSubAssemblyForm();
    } catch (err) {
      setError(err.message);
    }
  };

  // Swap functionality functions
  const handleSwapClick = async (node) => {
    try {
      setSwapLoading(true);
      setSwapTarget(node);
      
      // Fetch available components with the same BOM category
      const response = await fetch(`${API_BASE_URL}/api/bom/components/same-category/${node.BOM_category}`);
      if (!response.ok) {
        throw new Error('Failed to fetch available components for swapping');
      }
      
      const components = await response.json();
      
      // Filter out the current component
      const filteredComponents = components.filter(comp => comp.component_id !== node.component_id);
      setAvailableSwapComponents(filteredComponents);
      setSelectedSwapComponent('');
      setShowSwapModal(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setSwapLoading(false);
    }
  };

  const handleSwapComponent = async (e) => {
    e.preventDefault();
    
    if (!selectedSwapComponent) {
      setError('Please select a component to swap with');
      return;
    }

    try {
      setSwapLoading(true);
      
      const response = await fetch(`${API_BASE_URL}/api/bom/entries/${swapTarget.id}/swap`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          new_component_id: selectedSwapComponent
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to swap component');
      }

      const result = await response.json();
      
      // Refresh the BOM structure
      await fetchBomStructure(selectedSku);
      
      // Close the modal and reset state
      setShowSwapModal(false);
      setSwapTarget(null);
      setSelectedSwapComponent('');
      setAvailableSwapComponents([]);
      
      // Show success message
      setError(null);
      alert(`Successfully swapped ${result.old_component_id} with ${result.new_component_id}`);
      
    } catch (err) {
      setError(err.message);
    } finally {
      setSwapLoading(false);
    }
  };

  const resetSwapModal = () => {
    setShowSwapModal(false);
    setSwapTarget(null);
    setSelectedSwapComponent('');
    setAvailableSwapComponents([]);
    setSwapLoading(false);
  };

  // Render BOM tree recursively
  const renderBomTree = (nodes, level = 0) => {
    return nodes.map((node) => {
      // Check if this node is a sub-assembly (has children but is not a SKU)
      const isSubAssembly = node.children && node.children.length > 0 && !node.sku_name;
      
      return (
        <div key={node.id} className="bom-tree-node" style={{ marginLeft: `${level * 20}px` }}>
          <div className="bom-tree-item">
            <div className="bom-tree-content">
              <span className="bom-tree-icon">
                {node.children && node.children.length > 0 ? '📦' : '🔧'}
              </span>
              <div className="bom-tree-details">
                <div className="bom-tree-name">
                  {node.sku_name || node.component_name || node.component_id}
                </div>
                <div className="bom-tree-quantity">
                  {node.BOM_quantity} {node.BOM_unit} ({node.BOM_category})
                </div>
              </div>
            </div>
            <div className="bom-tree-actions">
              <button 
                className="action-btn edit"
                onClick={() => handleEditBomEntry(node)}
                title="Edit BOM Entry"
              >
                ✏️
              </button>
              <button 
                className="action-btn delete"
                onClick={() => handleDeleteBomEntry(node.id)}
                title="Delete BOM Entry"
              >
                🗑️
              </button>
            </div>
          </div>
          {node.children && node.children.length > 0 && (
            <div className="bom-tree-children">
              {renderBomTree(node.children, level + 1)}
            </div>
          )}
        </div>
      );
    });
  };

  // Filter and search components
  const filteredComponents = components.filter(component => {
    const matchesSearch = 
      component.component_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      component.component_description?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesSupplier = !filterSupplier || component.supplier === filterSupplier;
    
    return matchesSearch && matchesSupplier;
  });

  // Filter and search sub-assemblies
  const filteredSubAssemblies = subAssemblies.filter(subAssembly => {
    const matchesSearch = 
      subAssembly.component_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      subAssembly.component_description?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesSupplier = !filterSupplier || subAssembly.supplier === filterSupplier;
    
    return matchesSearch && matchesSupplier;
  });

  // Get unique suppliers for filter
  const suppliers = [...new Set(components.map(component => component.supplier).filter(Boolean))];

  // Sub-assemblies section
  const renderSubAssemblies = () => {
    return (
      <div className="sub-assemblies-section">
        <div className="section-header">
          <h3>Sub-assemblies Management</h3>
          <p>Manage sub-assemblies table entries</p>
          <button 
            className="add-button"
            onClick={() => setShowSubAssemblyTableForm(true)}
          >
            + Add New Sub-assembly
          </button>
        </div>

        {subAssemblyTableLoading ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading sub-assemblies data...</p>
          </div>
        ) : (
          <div className="table-container">
            <table className="sub-assemblies-table">
              <thead>
                <tr>
                  <th>Index</th>
                  <th>Sub Assembly</th>
                  <th>Category</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {subAssembliesTable.length === 0 ? (
                  <tr>
                    <td colSpan="4" className="no-data">
                      No sub-assemblies found. Click "Add New Sub-assembly" to get started.
                    </td>
                  </tr>
                ) : (
                  subAssembliesTable.map((subAssembly) => (
                    <tr key={subAssembly.generic_index}>
                      <td className="sub-assembly-index">{subAssembly.generic_index}</td>
                      <td className="sub-assembly-name">{subAssembly.sub_assembly}</td>
                      <td className="sub-assembly-category">{subAssembly.sub_category}</td>
                      <td className="actions">
                        <button 
                          className="action-btn edit"
                          onClick={() => handleEditSubAssemblyTable(subAssembly)}
                          title="Edit Sub-assembly"
                        >
                          ✏️
                        </button>
                        <button 
                          className="action-btn delete"
                          onClick={() => handleDeleteSubAssemblyTable(subAssembly.generic_index)}
                          title="Delete Sub-assembly"
                        >
                          🗑️
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Add/Edit Sub-assembly Form */}
        {showSubAssemblyTableForm && (
          <div className="form-overlay">
            <div className="form-container">
              <div className="form-header">
                <h2>{editingSubAssemblyTable ? 'Edit Sub-assembly' : 'Add New Sub-assembly'}</h2>
                <button className="close-button" onClick={resetSubAssemblyTableForm}>×</button>
              </div>
              
              <form onSubmit={editingSubAssemblyTable ? handleUpdateSubAssemblyTable : handleAddSubAssemblyTable}>
                <div className="form-grid">
                  <div className="form-group full-width">
                    <label>Sub Assembly Name *</label>
                    <input
                      type="text"
                      name="sub_assembly"
                      value={subAssemblyTableFormData.sub_assembly}
                      onChange={handleSubAssemblyTableInputChange}
                      required
                      placeholder="Enter sub-assembly name"
                    />
                  </div>

                  <div className="form-group full-width">
                    <label>Category *</label>
                    <input
                      type="text"
                      name="sub_category"
                      value={subAssemblyTableFormData.sub_category}
                      onChange={handleSubAssemblyTableInputChange}
                      required
                      placeholder="Enter category (e.g., Mechanical, Electronics, etc.)"
                    />
                  </div>
                </div>

                <div className="form-actions">
                  <button type="button" onClick={resetSubAssemblyTableForm} className="cancel-button">
                    Cancel
                  </button>
                  <button type="submit" className="save-button" disabled={subAssemblyTableLoading}>
                    {subAssemblyTableLoading ? 'Saving...' : (editingSubAssemblyTable ? 'Update Sub-assembly' : 'Add Sub-assembly')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Handle child quantity change
  const handleChildQuantityChange = async (bomId, newQuantity) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/bom/entries/${bomId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          quantity: parseFloat(newQuantity)
        }),
      });

      if (response.ok) {
        // Refresh the sub-assembly structure
        if (selectedSubAssembly) {
          fetchSubAssemblyStructure(selectedSubAssembly);
        }
      } else {
        console.error('Failed to update quantity');
      }
    } catch (error) {
      console.error('Error updating quantity:', error);
    }
  };

  // Handle sub-assembly update
  const handleSubAssemblyUpdate = async (e) => {
    e.preventDefault();
    
    try {
      // Check if the component exists in the components table
      const componentExists = components.some(comp => comp.component_id === editingSubAssembly.component_id);
      
      if (componentExists) {
        // Update existing component
        const response = await fetch(`${API_BASE_URL}/api/components/${editingSubAssembly.component_id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            component_description: editingSubAssembly.component_description,
            supplier: editingSubAssembly.supplier,
            supplier_number: editingSubAssembly.supplier_number,
            MOQ: editingSubAssembly.MOQ,
            lead_time_weeks: editingSubAssembly.lead_time_weeks
          }),
        });

        if (response.ok) {
          // Refresh components and sub-assembly structure
          fetchComponents();
          if (selectedSubAssembly) {
            fetchSubAssemblyStructure(selectedSubAssembly);
          }
          setEditingSubAssembly(null);
        } else {
          console.error('Failed to update sub-assembly');
        }
      } else {
        // Create new component entry
        const response = await fetch(`${API_BASE_URL}/api/components`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            component_id: editingSubAssembly.component_id,
            component_description: editingSubAssembly.component_description,
            supplier: editingSubAssembly.supplier,
            supplier_number: editingSubAssembly.supplier_number,
            MOQ: editingSubAssembly.MOQ,
            lead_time_weeks: editingSubAssembly.lead_time_weeks
          }),
        });

        if (response.ok) {
          // Refresh components and sub-assembly structure
          fetchComponents();
          if (selectedSubAssembly) {
            fetchSubAssemblyStructure(selectedSubAssembly);
          }
          setEditingSubAssembly(null);
        } else {
          console.error('Failed to create sub-assembly component');
        }
      }
    } catch (error) {
      console.error('Error updating sub-assembly:', error);
    }
  };

  // Handle adding child component
  const handleAddChildComponent = async (e) => {
    e.preventDefault();
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/bom/entry`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          parent_id: selectedSubAssembly,
          component_id: newChildComponent.component_id,
          quantity: newChildComponent.quantity
        }),
      });

      if (response.ok) {
        // Refresh the sub-assembly structure
        fetchSubAssemblyStructure(selectedSubAssembly);
        setShowAddChildForm(false);
        setNewChildComponent({ component_id: '', quantity: 1 });
      } else {
        console.error('Failed to add child component');
      }
    } catch (error) {
      console.error('Error adding child component:', error);
    }
  };

  // Handle sub-assembly table form input changes
  const handleSubAssemblyTableInputChange = (e) => {
    const { name, value } = e.target;
    setSubAssemblyTableFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Reset sub-assembly table form
  const resetSubAssemblyTableForm = () => {
    setSubAssemblyTableFormData({
      sub_assembly: '',
      sub_category: ''
    });
    setEditingSubAssemblyTable(null);
    setShowSubAssemblyTableForm(false);
  };

  // Add new sub-assembly to table
  const handleAddSubAssemblyTable = async (e) => {
    e.preventDefault();
    try {
      setSubAssemblyTableLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/sub-assemblies`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(subAssemblyTableFormData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to add sub-assembly');
      }

      await fetchSubAssembliesTable();
      resetSubAssemblyTableForm();
    } catch (err) {
      setError(err.message);
    } finally {
      setSubAssemblyTableLoading(false);
    }
  };

  // Update existing sub-assembly in table
  const handleUpdateSubAssemblyTable = async (e) => {
    e.preventDefault();
    try {
      setSubAssemblyTableLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/sub-assemblies/${editingSubAssemblyTable.generic_index}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(subAssemblyTableFormData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update sub-assembly');
      }

      await fetchSubAssembliesTable();
      resetSubAssemblyTableForm();
    } catch (err) {
      setError(err.message);
    } finally {
      setSubAssemblyTableLoading(false);
    }
  };

  // Delete sub-assembly from table
  const handleDeleteSubAssemblyTable = async (genericIndex) => {
    if (!window.confirm('Are you sure you want to delete this sub-assembly?')) {
      return;
    }

    try {
      setSubAssemblyTableLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/sub-assemblies/${genericIndex}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete sub-assembly');
      }

      await fetchSubAssembliesTable();
    } catch (err) {
      setError(err.message);
    } finally {
      setSubAssemblyTableLoading(false);
    }
  };

  // Start editing sub-assembly in table
  const handleEditSubAssemblyTable = (subAssembly) => {
    setSubAssemblyTableFormData({
      sub_assembly: subAssembly.sub_assembly,
      sub_category: subAssembly.sub_category
    });
    setEditingSubAssemblyTable(subAssembly);
    setShowSubAssemblyTableForm(true);
  };

  // Handle Add Item form input changes
  const handleAddItemInputChange = (e) => {
    const { name, value } = e.target;
    setAddItemFormData(prev => ({
      ...prev,
      [name]: name === 'BOM_quantity' ? parseFloat(value) || 0 : value
    }));
  };

  // Reset Add Item form
  const resetAddItemForm = () => {
    setAddItemFormData({
      parent_id: '',
      component_id: '',
      BOM_quantity: 1,
      BOM_category: '',
      BOM_unit: ''
    });
    setShowAddItemModal(false);
  };

  // Handle Add Item form submission
  const handleAddItem = async (e) => {
    e.preventDefault();
    
    if (!addItemFormData.parent_id || !addItemFormData.component_id || !addItemFormData.BOM_category || !addItemFormData.BOM_unit) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      setAddItemLoading(true);
      
      const response = await fetch(`${API_BASE_URL}/api/bom/entries`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(addItemFormData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to add BOM entry');
      }

      resetAddItemForm();
      setError(null);
      alert('BOM entry added successfully!');
      
      // Refresh BOM structure to show the new item immediately
      if (selectedSku) {
        await fetchBomStructure(selectedSku);
      }
      
    } catch (err) {
      setError(err.message);
    } finally {
      setAddItemLoading(false);
    }
  };

  return (
    <div className="bom-components">
      <div className="page-header">
        <div className="header-content">
          <div className="header-title">
            <h1>BOM & Components Manager</h1>
          </div>
          <div className="header-tips">
            <ul>
              <li>Manage Bill of Materials structure and components</li>
              <li>Configure sub-assemblies and component relationships</li>
              <li>Track component suppliers and specifications</li>
            </ul>
          </div>
        </div>
        <div className="header-separator"></div>
      </div>

      <div className="page-content">

      {error && (
        <div className="error-message">
          <span>❌ {error}</span>
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      <div className="tab-container">
        <div className="tab-buttons">
          <button 
            className={`tab-button ${activeTab === 'bom' ? 'active' : ''}`}
            onClick={() => setActiveTab('bom')}
          >
            Bill of Materials
          </button>
          <button 
            className={`tab-button ${activeTab === 'components' ? 'active' : ''}`}
            onClick={() => setActiveTab('components')}
          >
            Components
          </button>
          <button 
            className={`tab-button ${activeTab === 'sub-assemblies' ? 'active' : ''}`}
            onClick={() => setActiveTab('sub-assemblies')}
          >
            Sub-assemblies
          </button>
        </div>

        <div className="tab-content">
          {activeTab === 'bom' && (
            <div className="bom-section">
              <div className="section-header">
                <h2>Bill of Materials Management</h2>
                {selectedSku && (
                  <div className="action-buttons">
                    <button 
                      className="add-button secondary"
                      onClick={() => {
                        fetchAddItemData();
                        setShowAddItemModal(true);
                      }}
                    >
                      + Add Item
                    </button>
                  </div>
                )}
              </div>
              
              <div className="bom-controls">
                <div className="sku-selector">
                  <label>Select SKU:</label>
                  <select 
                    value={selectedSku} 
                    onChange={(e) => setSelectedSku(e.target.value)}
                    className="sku-select"
                  >
                    <option value="">Choose a SKU...</option>
                    {bomSkus.map(sku => (
                      <option key={sku.IGD_SKU_number} value={sku.IGD_SKU_number}>
                        {sku.IGD_SKU_number} - {sku.IGD_Name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {selectedSku && (
                <div className="bom-content">
                  {bomLoading ? (
                    <div className="loading-container">
                      <div className="loading-spinner"></div>
                      <p>Loading BOM structure...</p>
                    </div>
                  ) : (
                    <div className="bom-structure-container">
                      <div className="bom-header">
                        <h3>BOM Structure for: {bomStructure?.sku_name || selectedSku}</h3>
                      </div>
                      
                      {bomStructure?.structure && bomStructure.structure.length > 0 ? (
                        <div className="bom-tree">
                          {renderBomTree(bomStructure.structure)}
                        </div>
                      ) : (
                        <div className="no-bom-data">
                          <p>No BOM structure defined for this SKU.</p>
                          <p>Click "Add Component to BOM" to start building the structure.</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* BOM Add/Edit Form */}
              {showBomForm && (
                <div className="form-overlay">
                  <div className="form-container">
                    <div className="form-header">
                      <h2>
                        {editingBomEntry ? 'Edit BOM Entry' : 'Add Component to BOM'}
                        {!editingBomEntry && bomFormData.parent_id && (
                          <span className="parent-indicator">
                            {' '}under {allAvailableParents.find(p => p.id === bomFormData.parent_id)?.name || bomFormData.parent_id}
                          </span>
                        )}
                      </h2>
                      <button className="close-button" onClick={resetBomForm}>×</button>
                    </div>
                    
                    <form onSubmit={editingBomEntry ? handleUpdateBomEntry : handleAddBomEntry}>
                      <div className="form-grid">
                        <div className="form-group full-width">
                          <label>Parent *</label>
                          <select
                            name="parent_id"
                            value={bomFormData.parent_id}
                            onChange={handleBomInputChange}
                            required
                            disabled={!!editingBomEntry}
                          >
                            <option value="">Select a parent...</option>
                            {allAvailableParents.map(parent => (
                              <option key={parent.id} value={parent.id}>
                                {parent.name} ({parent.type})
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className="form-group full-width">
                          <label>Component *</label>
                          <select
                            name="component_id"
                            value={bomFormData.component_id}
                            onChange={handleBomInputChange}
                            required
                            disabled={!!editingBomEntry}
                          >
                            <option value="">Select a component...</option>
                            {availableComponents.map(comp => (
                              <option key={comp.component_id} value={comp.component_id}>
                                {comp.component_id} - {comp.component_description}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className="form-group">
                          <label>Quantity *</label>
                          <input
                            type="number"
                            name="BOM_quantity"
                            value={bomFormData.BOM_quantity}
                            onChange={handleBomInputChange}
                            required
                            min="0.01"
                            step="0.0000000001"
                          />
                        </div>

                        <div className="form-group">
                          <label>Category *</label>
                          <input
                            type="text"
                            name="BOM_category"
                            value={bomFormData.BOM_category}
                            onChange={handleBomInputChange}
                            required
                            placeholder="e.g., Component, Sub-assembly, Packaging, etc."
                          />
                        </div>

                        <div className="form-group">
                          <label>Unit *</label>
                          <input
                            type="text"
                            name="BOM_unit"
                            value={bomFormData.BOM_unit}
                            onChange={handleBomInputChange}
                            required
                            placeholder="e.g., units, kg, oz"
                          />
                        </div>
                      </div>

                      <div className="form-actions">
                        <button type="button" onClick={resetBomForm} className="cancel-button">
                          Cancel
                        </button>
                        <button type="submit" className="save-button">
                          {editingBomEntry ? 'Update BOM Entry' : 'Add to BOM'}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              )}

              {/* Sub-Assembly Add Form */}
              {showSubAssemblyForm && (
                <div className="form-overlay">
                  <div className="form-container">
                    <div className="form-header">
                      <h2>
                        Add Sub-Assembly to BOM
                        {subAssemblyFormData.parent_id && (
                          <span className="parent-indicator">
                            {' '}under {allAvailableParents.find(p => p.id === subAssemblyFormData.parent_id)?.name || subAssemblyFormData.parent_id}
                          </span>
                        )}
                      </h2>
                      <button className="close-button" onClick={resetSubAssemblyForm}>×</button>
                    </div>
                    
                    <form onSubmit={handleAddSubAssembly}>
                      <div className="form-grid">
                        <div className="form-group full-width">
                          <label>Parent *</label>
                          <select
                            name="parent_id"
                            value={subAssemblyFormData.parent_id}
                            onChange={handleSubAssemblyInputChange}
                            required
                          >
                            <option value="">Select a parent...</option>
                            {allAvailableParents.map(parent => (
                              <option key={parent.id} value={parent.id}>
                                {parent.name} ({parent.type})
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className="form-group full-width">
                          <label>Sub-Assembly ID *</label>
                          <input
                            type="text"
                            name="component_id"
                            value={subAssemblyFormData.component_id}
                            onChange={handleSubAssemblyInputChange}
                            required
                            placeholder="Enter a unique ID for the sub-assembly"
                          />
                        </div>

                        <div className="form-group">
                          <label>Quantity *</label>
                          <input
                            type="number"
                            name="BOM_quantity"
                            value={subAssemblyFormData.BOM_quantity}
                            onChange={handleSubAssemblyInputChange}
                            required
                            min="0.01"
                            step="0.0000000001"
                          />
                        </div>

                        <div className="form-group">
                          <label>Category *</label>
                          <input
                            type="text"
                            name="BOM_category"
                            value={subAssemblyFormData.BOM_category}
                            onChange={handleSubAssemblyInputChange}
                            required
                            placeholder="e.g., Sub-assembly, Assembly, etc."
                          />
                        </div>

                        <div className="form-group">
                          <label>Unit *</label>
                          <input
                            type="text"
                            name="BOM_unit"
                            value={subAssemblyFormData.BOM_unit}
                            onChange={handleSubAssemblyInputChange}
                            required
                            placeholder="e.g., units, kg, oz"
                          />
                        </div>
                      </div>

                      <div className="form-actions">
                        <button type="button" onClick={resetSubAssemblyForm} className="cancel-button">
                          Cancel
                        </button>
                        <button type="submit" className="save-button">
                          Add Sub-Assembly
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              )}

              {/* Add Item Modal */}
              {showAddItemModal && (
                <div className="form-overlay">
                  <div className="form-container">
                    <div className="form-header">
                      <h2>Add BOM Item</h2>
                      <button className="close-button" onClick={resetAddItemForm}>×</button>
                    </div>
                    
                    <form onSubmit={handleAddItem}>
                      <div className="form-grid">
                        <div className="form-group full-width">
                          <label>Parent (Component/SKU/Sub-assembly) *</label>
                          <select
                            name="parent_id"
                            value={addItemFormData.parent_id}
                            onChange={handleAddItemInputChange}
                            required
                            disabled={addItemLoading}
                          >
                            <option value="">Select a parent from the current BOM...</option>
                            {availableParents.map((parent) => (
                              <option key={parent.id} value={parent.id}>
                                {parent.name} ({parent.type})
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className="form-group full-width">
                          <label>Component/SKU/Sub-assembly *</label>
                          <select
                            name="component_id"
                            value={addItemFormData.component_id}
                            onChange={handleAddItemInputChange}
                            required
                            disabled={addItemLoading}
                          >
                            <option value="">Select a component, SKU, or sub-assembly...</option>
                            
                            {/* Components */}
                            <optgroup label="Components">
                              {availableComponents.map((component) => (
                                <option key={`comp-${component.component_id}`} value={component.component_id}>
                                  {component.component_description} ({component.component_id}) - Component
                                </option>
                              ))}
                            </optgroup>
                            
                            {/* SKUs */}
                            <optgroup label="SKUs">
                              {availableSkus.map((sku) => (
                                <option key={`sku-${sku.IGD_SKU_number}`} value={sku.IGD_SKU_number}>
                                  {sku.IGD_Name} ({sku.IGD_SKU_number}) - SKU
                                </option>
                              ))}
                            </optgroup>
                            
                            {/* Sub-assemblies */}
                            <optgroup label="Sub-assemblies">
                              {availableSubAssemblies.map((subAssembly) => (
                                <option key={`sub-${subAssembly.sub_assembly}`} value={subAssembly.sub_assembly}>
                                  {subAssembly.sub_assembly} ({subAssembly.sub_category}) - Sub-assembly
                                </option>
                              ))}
                            </optgroup>
                          </select>
                        </div>

                        <div className="form-group">
                          <label>Quantity *</label>
                          <input
                            type="number"
                            name="BOM_quantity"
                            value={addItemFormData.BOM_quantity}
                            onChange={handleAddItemInputChange}
                            required
                            min="0.01"
                            step="0.0000000001"
                            disabled={addItemLoading}
                          />
                        </div>

                        <div className="form-group">
                          <label>Category *</label>
                          <div className="select-with-add">
                            <input
                              type="text"
                              name="BOM_category"
                              value={addItemFormData.BOM_category}
                              onChange={handleAddItemInputChange}
                              list="existing-categories"
                              required
                              disabled={addItemLoading}
                              placeholder="Select or type a category..."
                            />
                            <datalist id="existing-categories">
                              {existingCategories.map((category) => (
                                <option key={category} value={category} />
                              ))}
                            </datalist>
                            <small>Select from existing categories or type a new one</small>
                          </div>
                        </div>

                        <div className="form-group">
                          <label>Unit *</label>
                          <div className="select-with-add">
                            <input
                              type="text"
                              name="BOM_unit"
                              value={addItemFormData.BOM_unit}
                              onChange={handleAddItemInputChange}
                              list="existing-units"
                              required
                              disabled={addItemLoading}
                              placeholder="Select or type a unit..."
                            />
                            <datalist id="existing-units">
                              {existingUnits.map((unit) => (
                                <option key={unit} value={unit} />
                              ))}
                            </datalist>
                            <small>Select from existing units or type a new one</small>
                          </div>
                        </div>
                      </div>

                      <div className="form-actions">
                        <button type="button" onClick={resetAddItemForm} className="cancel-button" disabled={addItemLoading}>
                          Cancel
                        </button>
                        <button type="submit" className="save-button" disabled={addItemLoading}>
                          {addItemLoading ? 'Adding...' : 'Add Item'}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'sub-assemblies' && (
            renderSubAssemblies()
          )}

          {activeTab === 'components' && (
            <div className="components-section">
              {loading ? (
                <div className="loading-container">
                  <div className="loading-spinner"></div>
                  <p>Loading component data...</p>
                </div>
              ) : (
                <>
                  <div className="controls-section">
                    <div className="search-filters">
                      <div className="search-box">
                        <input
                          type="text"
                          placeholder="Search by Component ID..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        <span className="search-icon">🔍</span>
                      </div>
                      
                      <select
                        value={filterSupplier}
                        onChange={(e) => setFilterSupplier(e.target.value)}
                        className="supplier-filter"
                      >
                        <option value="">All Suppliers</option>
                        {suppliers.map(supplier => (
                          <option key={supplier} value={supplier}>{supplier}</option>
                        ))}
                      </select>
                    </div>

                    <div className="filter-info">
                      <small>
                        Showing {filteredComponents.length} of {components.length} components
                        {searchTerm && ` (filtered by search: "${searchTerm}")`}
                        {filterSupplier && ` (filtered by supplier: "${filterSupplier}")`}
                      </small>
                    </div>

                    <button 
                      className="add-button"
                      onClick={() => setShowAddForm(true)}
                    >
                      + Add New Component
                    </button>
                  </div>

                  {/* Add/Edit Form */}
                  {showAddForm && (
                    <div className="form-overlay">
                      <div className="form-container">
                        <div className="form-header">
                          <h2>{editingComponent ? 'Edit Component' : 'Add New Component'}</h2>
                          <button className="close-button" onClick={resetForm}>×</button>
                        </div>
                        
                        <form onSubmit={editingComponent ? handleUpdateComponent : handleAddComponent}>
                          <div className="form-grid">
                            <div className="form-group">
                              <label>Component ID *</label>
                              <input
                                type="text"
                                name="component_id"
                                value={formData.component_id}
                                onChange={handleInputChange}
                                required
                                disabled={!!editingComponent}
                              />
                            </div>

                            <div className="form-group">
                              <label>Supplier *</label>
                              <input
                                type="text"
                                name="supplier"
                                value={formData.supplier}
                                onChange={handleInputChange}
                                required
                              />
                            </div>

                            <div className="form-group">
                              <label>Supplier Number</label>
                              <input
                                type="text"
                                name="supplier_number"
                                value={formData.supplier_number}
                                onChange={handleInputChange}
                              />
                            </div>

                            <div className="form-group">
                              <label>MOQ (Minimum Order Quantity)</label>
                              <input
                                type="number"
                                name="MOQ"
                                value={formData.MOQ}
                                onChange={handleInputChange}
                                min="1"
                              />
                            </div>

                            <div className="form-group">
                              <label>Lead Time (weeks)</label>
                              <input
                                type="number"
                                name="lead_time_weeks"
                                value={formData.lead_time_weeks}
                                onChange={handleInputChange}
                                min="0"
                              />
                            </div>

                            <div className="form-group">
                              <label>Is Inventoried</label>
                              <select
                                name="is_inventoried"
                                value={formData.is_inventoried.toString()}
                                onChange={(e) => setFormData(prev => ({
                                  ...prev,
                                  is_inventoried: e.target.value === 'true'
                                }))}
                              >
                                <option value="true">Yes</option>
                                <option value="false">No</option>
                              </select>
                            </div>

                            <div className="form-group full-width">
                              <label>Component Description</label>
                              <textarea
                                name="component_description"
                                value={formData.component_description}
                                onChange={handleInputChange}
                                rows="3"
                              />
                            </div>
                          </div>

                          <div className="form-actions">
                            <button type="button" onClick={resetForm} className="cancel-button">
                              Cancel
                            </button>
                            <button type="submit" className="save-button">
                              {editingComponent ? 'Update Component' : 'Add Component'}
                            </button>
                          </div>
                        </form>
                      </div>
                    </div>
                  )}

                  {/* Components Table */}
                  <div className="table-container">
                    <table className="components-table">
                      <thead>
                        <tr>
                          <th>Component ID</th>
                          <th>Description</th>
                          <th>Supplier</th>
                          <th>Supplier Number</th>
                          <th>MOQ</th>
                          <th>Lead Time (weeks)</th>
                          <th>Is Inventoried</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredComponents.length === 0 ? (
                          <tr>
                            <td colSpan="8" className="no-data">
                              {searchTerm || filterSupplier ? 'No components match your search criteria' : 'No components found'}
                            </td>
                          </tr>
                        ) : (
                          filteredComponents.map(component => (
                              <tr key={component.component_id}>
                                <td className="component-id">{component.component_id}</td>
                                <td className="component-description">{component.component_description || '-'}</td>
                                <td className="component-supplier">{component.supplier || '-'}</td>
                                <td className="component-supplier-number">{component.supplier_number || '-'}</td>
                                <td className="component-moq">{component.MOQ || '-'}</td>
                                <td className="component-lead-time">{component.lead_time_weeks || '-'}</td>
                                <td className="component-inventoried">
                                  <span className={`inventory-status ${component.is_inventoried ? 'yes' : 'no'}`}>
                                    {component.is_inventoried ? 'Yes' : 'No'}
                                  </span>
                                </td>
                                <td className="actions">
                                  <button 
                                    className="action-btn edit"
                                    onClick={() => handleEditComponent(component)}
                                    title="Edit Component"
                                  >
                                    ✏️
                                  </button>
                                  <button 
                                    className="action-btn delete"
                                    onClick={() => handleDeleteComponent(component.component_id)}
                                    title="Delete Component"
                                  >
                                    🗑️
                                  </button>
                                </td>
                              </tr>
                            ))
                        )}
                      </tbody>
                    </table>
                  </div>

                  <div className="summary">
                    <p>Showing {filteredComponents.length} of {components.length} components</p>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
      </div>

      {/* Swap Component Modal */}
      {showSwapModal && (
        <div className="form-overlay">
          <div className="form-container">
            <div className="form-header">
              <h2>Swap Component</h2>
              <button className="close-button" onClick={resetSwapModal}>×</button>
            </div>
            
            <div className="swap-info">
              <p><strong>Current Component:</strong> {swapTarget?.component_name || swapTarget?.component_id}</p>
              <p><strong>Category:</strong> {swapTarget?.BOM_category}</p>
              <p><strong>Quantity:</strong> {swapTarget?.BOM_quantity} {swapTarget?.BOM_unit}</p>
            </div>
            
            <form onSubmit={handleSwapComponent}>
              <div className="form-group full-width">
                <label>Select Replacement Component *</label>
                <select
                  value={selectedSwapComponent}
                  onChange={(e) => setSelectedSwapComponent(e.target.value)}
                  required
                  disabled={swapLoading}
                >
                  <option value="">Choose a component...</option>
                  {availableSwapComponents.map((component) => (
                    <option key={component.component_id} value={component.component_id}>
                      {component.component_description} ({component.component_id}) - {component.child_count} children
                    </option>
                  ))}
                </select>
                <small>
                  Only components with the same category ({swapTarget?.BOM_category}) are shown.
                  {swapLoading && ' Loading...'}
                </small>
              </div>

              <div className="swap-warning">
                <p><strong>⚠️ Warning:</strong> This action will:</p>
                <ul>
                  <li>Replace the current component with the selected one</li>
                  <li>Remove all children of the current component</li>
                  <li>Add all children of the new component</li>
                  <li>This action cannot be undone</li>
                </ul>
              </div>

              <div className="form-actions">
                <button type="button" onClick={resetSwapModal} className="cancel-button" disabled={swapLoading}>
                  Cancel
                </button>
                <button type="submit" className="save-button" disabled={swapLoading || !selectedSwapComponent}>
                  {swapLoading ? 'Swapping...' : 'Swap Component'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default BOMComponents; 